// IZI Accidental Damage Plan — Terms & Conditions copy.
// Source of truth: "IZI Accidental Plan TnCs page" design export. Keep wording exact.

import {
  clause,
  contactSection,
  dash,
  note,
  slaSection,
  step,
  type TncHero,
  type TncSection,
} from "@/components/tnc/tncTypes";

export const tncHero: TncHero = {
  breadcrumb: "IZI Accidental Damage Plan",
  current: "Terms & Conditions",
  title: "Terms and Conditions",
  lines: [
    "Accidental Damage Protection Plan",
    "For products purchased on IZI Cart.",
  ],
};

export const tncFooterNote =
  "IZI Accidental Damage Plan is administered by EmbedMike India Private Limited (Protega) on behalf of IZI Cart. It is a service plan, not an insurance policy, and the Terms and Conditions govern in full.";

export const tncSections: TncSection[] = [
  {
    kind: "clauses",
    id: "plan-introduction",
    title: "Plan introduction",
    blocks: [
      clause(
        "The Protega × IZICART Accidental Damage Protection Plan (“Plan”) is a post-purchase protection service designed to provide coverage against sudden, unforeseen and unintentional physical damage to eligible products during normal personal or household use, subject to these Terms and Conditions (“Terms”)."
      ),
      clause(
        "This Plan is a service contract administered by EmbedMike India Private Limited (“Protega”) on behalf of IZI Cart. This Plan does not constitute an insurance policy and does not replace, extend or override the manufacturer’s standard warranty, seller warranty or any statutory consumer rights. Protega may appoint Authorised Service Partners to perform any part of the Plan and remains responsible to the Customer for its obligations under the Plan."
      ),
      clause(
        "The Plan covers accidental physical damage arising from external events such as drops, falls, collisions or similar incidents that result in functional or structural impairment of the product. Coverage is limited to accidental damage events that are external, visible and verifiable. Any damage that cannot reasonably be attributed to a defined accidental event is not covered."
      ),
      clause(
        "Coverage under this Plan begins when the order for the Product and Plan is placed and remains valid for a period of twelve (12) months or twenty-four (24) months, as selected by the customer at the time of purchase."
      ),
      clause(
        "The Plan is valid only within the territorial limits of India. All inspections, diagnostics, repairs, replacements, and logistics shall be performed exclusively through Protega’s authorised service and logistics partners."
      ),
      clause(
        "Each approved accidental damage claim shall be subject to a deductible equal to ten percent (10%) of the Invoice Value or ₹500 (Indian Rupees Five Hundred), whichever is lower, or as stated in your Certificate of Protection. The deductible must be paid before any service fulfilment begins and is non-refundable once the RMA is issued.",
        "Deductible:"
      ),
      clause(
        "The Plan applies to new, genuine consumer electronics purchased on IZI Cart that are eligible for the Plan at the time of purchase and are intended strictly for personal and non-commercial use. Drones are not eligible under this Plan."
      ),
      clause(
        "Coverage under this Plan is applicable only when the product is used strictly in accordance with the manufacturer’s intended purpose, operating instructions, safety guidelines, environmental limitations and usage conditions as described in the user manual, product packaging or official documentation."
      ),
      clause(
        "The Certificate of Protection, these Terms, the invoice for the Product and the Plan, and the payment confirmation together form the Plan. If the Certificate of Protection and these Terms differ, these Terms prevail.",
        "Documents forming the Plan:"
      ),
      clause(
        "Although cover starts when the order is placed, the Plan covers only accidental damage that occurs after the Product has been delivered to the Customer, while the Product is being used for its intended purpose in normal personal or household use. Any damage, loss or defect arising before or during shipping and delivery is not covered and is handled under the Merchant’s return and replacement policy.",
        "When damage is covered:"
      ),
      clause(
        "Where a claim is resolved by replacement or Store Credit, depreciation based on the age of the Product from the invoice date applies instead of the deductible, as set out below. Store Credit is issued for the Invoice Value less depreciation. For a replacement, the Customer pays the depreciation amount before the replacement is dispatched, or may choose Store Credit instead. Any deductible already paid is adjusted against the depreciation amount or added to the Store Credit. Claims resolved by repair are not subject to depreciation.",
        "Depreciation on replacement or store credit:"
      ),
      {
        kind: "table",
        head: ["Age of product at claim", "Depreciation"],
        rows: [
          ["Up to 6 months", "10%"],
          ["6 to 12 months", "20%"],
          ["12 to 18 months", "30%"],
          ["18 to 24 months", "40%"],
        ],
      },
    ],
  },
  {
    kind: "clauses",
    id: "customer-responsibilities",
    title: "Customer responsibilities",
    blocks: [
      note(
        "To remain eligible for coverage and services under this Plan, the customer must:"
      ),
      clause(
        "Retain the original purchase invoice, Plan certificate and payment confirmation."
      ),
      clause(
        "Report any accidental damage incident to Protega within seventy-two (72) hours of occurrence."
      ),
      clause(
        "Provide complete, accurate and truthful information regarding the incident and damage."
      ),
      clause(
        "Avoid self-repair, unauthorised servicing or engagement of third-party repair providers, and not hand the product to any service centre before Protega approves the claim."
      ),
      clause(
        "Operate, handle, store, charge and maintain the product strictly in accordance with the manufacturer’s user manual, safety instructions, accidental damage guidelines and stated limitations."
      ),
      clause(
        "Use the product only for its intended purpose, approved use cases and recommended operating environments."
      ),
      clause(
        "Avoid negligent, reckless, abusive, experimental or improper handling of the product."
      ),
      clause(
        "Avoid exposing the product to conditions, environments or usage scenarios not recommended or permitted by the manufacturer."
      ),
      clause(
        "Stop using the product after an incident where continued use may worsen the damage, and take reasonable steps to prevent further damage."
      ),
      clause(
        "Back up and remove all personal data, footage and memory cards before handing over the product for inspection or service."
      ),
      clause(
        "Cooperate fully during inspection, pickup and claim resolution processes, and hand over the product complete with all parts supplied with it."
      ),
      clause(
        "Keep your registered email address, mobile number and WhatsApp number up to date."
      ),
      note(
        "Failure to comply with these responsibilities may result in rejection of claims or denial of service."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "how-to-file-a-claim",
    title: "How to file a claim",
    blocks: [
      step("Step 1: Contact support"),
      note(
        "Customers must initiate the claim through one of the official support channels. Claims are accepted only through these two channels."
      ),
      dash("www.protega.ai/customer-login", "Portal:"),
      dash("+91 93240 95283", "WhatsApp:"),
      note(
        "Claims must be reported within seventy-two (72) hours of the accidental incident.",
        true
      ),
      step("Step 2: Submit documentation"),
      note("Customers must submit:"),
      dash("Purchase invoice and Accidental Damage Protection certificate"),
      dash("Original, unedited photographs or videos clearly showing the damage"),
      dash("A detailed description of the incident, including time, place and cause"),
      note(
        "Protega may request additional information, documents or a live video inspection before approving a claim. A claim will not be processed until the requested information is provided."
      ),
      step("Step 3: Claim verification"),
      note(
        "Protega will assess product eligibility, damage nature and coverage applicability. Initial assessment is typically completed within 24–48 working hours of receipt of complete documentation."
      ),
      step("Step 4: Deductible payment"),
      note(
        "Upon claim approval, a secure payment link is shared for payment of the applicable deductible."
      ),
      step("Step 5: RMA and pickup"),
      note(
        "A Return Merchandise Authorization (RMA) is generated and pickup is scheduled through Protega’s authorised logistics partners."
      ),
      step("Step 6: Resolution"),
      note(
        "Based on assessment, the product will be repaired using new or refurbished parts, replaced with a new, refurbished or functionally equivalent product, or settled via store credit. Standard repair cases are typically completed within 5–7 working days; replacement cases are subject to product availability. All timelines are subject to clause 8."
      ),
      clause(
        "The product must not be repaired, opened or handed to any service centre before Protega approves the claim in writing. Costs incurred before approval will not be reimbursed, and such a claim may be rejected.",
        "Approval before repair:"
      ),
      clause(
        "The deductible must be paid within 7 days of the payment link being sent. No pickup, inspection or repair takes place before payment. If the deductible is not paid within this time, the claim is treated as withdrawn and closed, and cannot be re-opened. No new claim will be accepted for the same incident. Where Protega has already incurred inspection or logistics costs, the Customer bears these costs before the product is returned.",
        "Deductible deadline:"
      ),
      clause(
        "Claims reported after 72 hours of the accidental incident may be rejected.",
        "Late reporting:"
      ),
      note("Claim limit: One approved claim per product per Policy Term.", true),
    ],
  },
  {
    kind: "clauses",
    id: "definitions",
    title: "Definitions",
    blocks: [
      clause(
        "The person purchasing an IZI Cart product with the Plan, whose name appears on the invoice.",
        "Customer:"
      ),
      clause("IZI Cart.", "Merchant:"),
      clause("EmbedMike India Private Limited.", "Administrator / Protega:"),
      clause(
        "The eligible product covered under this Plan and identified in the Certificate of Protection.",
        "Product:"
      ),
      clause(
        "Sudden, unforeseen and unintentional physical damage caused by an external event beyond the customer’s control, occurring while the Product is used for its intended purpose in a personal setting and in accordance with the manufacturer’s instructions.",
        "Accidental Damage:"
      ),
      clause(
        "Use, handling, exposure or operation of the product that demonstrates a clear disregard for reasonable care, manufacturer instructions, stated limitations or known safety precautions.",
        "Gross Negligence:"
      ),
      clause(
        "Use of the Product by the Customer for personal, non-business purposes only.",
        "Personal Use:"
      ),
      clause(
        "Use of the Product for paid work, rental, resale, or by or for a business, institution or office.",
        "Commercial Use:"
      ),
      clause(
        "A service provider approved by Protega for inspection, repair, replacement or logistics activities.",
        "Authorised Service Partner:"
      ),
      clause(
        "Duration of the Accidental Damage Plan (1-Year or 2-Year).",
        "Policy Term:"
      ),
      clause(
        "The price paid for the Product as shown on the purchase invoice, after any discounts and including applicable taxes, but excluding shipping or delivery charges, convenience or COD fees, installation charges, EMI interest or fees, and the Plan fee.",
        "Invoice Value:"
      ),
      clause(
        "The Invoice Value of the product. This is the maximum liability under the Plan, across all claims.",
        "Coverage Limit:"
      ),
      clause(
        "The reduction applied to the value of a replacement or Store Credit based on the age of the Product, as set out in clause 1.11.",
        "Depreciation:"
      ),
      clause(
        "One approved claim per product, up to the Coverage Limit.",
        "Claim Limit:"
      ),
      clause(
        "Return Merchandise Authorization issued after claim approval and deductible payment.",
        "RMA:"
      ),
      clause(
        "Where the repair cost exceeds the Invoice Value, or the Product cannot be repaired.",
        "Beyond Economic Repair (BER):"
      ),
      clause(
        "A non-cash voucher or credit usable only on IZI Cart, issued under clause 8.",
        "Store Credit:"
      ),
      clause(
        "A pincode in India where Protega or its Authorised Service Partners provide pickup or service.",
        "Serviceable Location:"
      ),
    ],
  },
  {
    kind: "clauses",
    id: "eligibility",
    title: "Eligibility",
    blocks: [
      clause(
        "Applicable to new, genuine, eligible non-drone products purchased through authorised IZI Cart sales channels in India."
      ),
      clause(
        "The Plan must be purchased together with the product through the Merchant’s eligible sales channel where the Plan is offered."
      ),
      clause(
        "The product must be new, fully functional and free from physical damage at the time of Plan purchase. Refurbished, open-box, second-hand and returned-and-resold units are not eligible."
      ),
      clause(
        "The product is used for Personal Use only. Commercial Use is excluded."
      ),
      clause(
        "The Customer is responsible for purchasing the Plan for the correct Product. Protega may reject a claim if the model or serial number of the Product claimed does not match the Plan."
      ),
      clause(
        "If a Plan is issued on a product that is not eligible, the Plan is void and the Plan fee is refunded in full."
      ),
      clause(
        "Coverage is limited to the main device unit and does not extend to separately sold accessories, memory cards or items subject to periodic replacement."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-covered",
    title: "What is covered",
    blocks: [
      note(
        "Subject to the terms and conditions of this Plan, coverage includes the following. Every item below is covered only where it results from a sudden, accidental external event.",
        true
      ),
      clause("Accidental drops, falls or impacts causing physical damage"),
      clause("Damage caused by a sudden, accidental external force or collision"),
      clause(
        "Accidental liquid spills or liquid ingress from a single, unintended event"
      ),
      clause(
        "cracked lens, display or housing, or broken ports, buttons or mounts on the body, caused by a covered accident",
        "Cameras (action and dash cameras):"
      ),
      clause(
        "broken arms, motors, joints or handles caused by a covered accident",
        "Gimbals:"
      ),
      clause(
        "cracked display or broken casing caused by a covered accident",
        "Smartwatches:"
      ),
      note(
        "The deductible applies to claims resolved by repair. Claims resolved by replacement or store credit are subject to depreciation instead (clause 1.11)."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-not-covered",
    title: "What is not covered",
    blocks: [
      note("The Plan does not cover:"),
      clause(
        "Manufacturing defects or performance issues covered under the manufacturer’s warranty."
      ),
      clause(
        "Cosmetic damage including scratches, dents, fading, discoloration or normal wear and tear, including lens scratches that do not affect image quality."
      ),
      clause("Theft, loss or unexplained disappearance of the product."),
      clause("Commercial, rental or professional use."),
      clause(
        "Damage caused by fire, floods, earthquakes, natural calamities, pests or environmental exposure."
      ),
      clause(
        "Damage arising from unauthorised repairs, disassembly or firmware modification."
      ),
      clause(
        "Damage caused by use outside manufacturer guidelines, intended purpose, approved environments or stated operating conditions."
      ),
      clause(
        "Damage resulting from gross negligence, reckless handling, misuse, deliberate abuse or disregard of product limitations."
      ),
      clause(
        "water damage from use beyond the rated depth or duration, use without the manufacturer’s housing where required, ports, seals or battery doors not fully closed, or salt water or chlorine not rinsed off as instructed.",
        "For waterproof or water-resistant products:"
      ),
      clause(
        "Battery wear or loss of battery capacity, charging faults, and damage from non-original chargers, cables or power sources, unless caused by a covered accident."
      ),
      clause("Memory cards, and loss of footage, data or settings."),
      clause(
        "Damage from mounts that were not properly secured, or from third-party mounts not approved by the manufacturer."
      ),
      clause(
        "Damage while the product is mounted on a drone, or used in racing or motorsport."
      ),
      clause(
        "Damage to dash cameras arising from a road accident or vehicle collision."
      ),
      clause(
        "damage from mounting equipment beyond the rated payload, or from operating the gimbal unbalanced or with its motors blocked.",
        "Gimbals:"
      ),
      clause(
        "straps and bands, screen scratches that do not affect use, and water damage beyond the rated water resistance, including hot water, saunas or diving beyond the rated depth.",
        "Smartwatches:"
      ),
      clause(
        "Power or electrical surges, voltage fluctuations or short circuits not caused by an external accident."
      ),
      clause(
        "Products whose serial number or label has been altered, defaced or removed."
      ),
      clause(
        "Damage, loss or defects arising before or during shipping and delivery, including transit damage discovered on unboxing. This is handled under IZI Cart’s return and replacement policy."
      ),
      clause(
        "Costs payable under any other warranty, insurance policy or protection plan."
      ),
      clause("Recalls and modifications required by the manufacturer."),
      clause(
        "Further damage caused by continued use of the product after the accidental event."
      ),
      clause("Pre-existing damage before Plan purchase."),
      clause(
        "Consequential or incidental losses, such as downtime, loss of use or replacement delays."
      ),
      clause(
        "Claims raised after the Policy Term, or without valid proof of the accident and deductible payment."
      ),
      clause(
        "Any claim involving fraud, misrepresentation or concealment of facts."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "service-procedures",
    title: "Service procedures & limit of liability",
    blocks: [
      clause(
        "Only one (1) approved accidental damage claim is permitted per product per Policy Term."
      ),
      clause(
        "Protega’s maximum liability under this Plan shall not exceed the Invoice Value of the product, across all claims."
      ),
      clause(
        "Upon issuance of a replacement product or full store credit, the Plan shall automatically terminate."
      ),
      clause(
        "Replacement units may be new, refurbished or functionally equivalent at Protega’s discretion, and may differ in colour or design."
      ),
      clause(
        "All decisions relating to repair, replacement or settlement shall be binding, subject to the dispute resolution clause in clause 11."
      ),
      clause(
        "No cash settlement is made under the Plan. Claims are resolved only by repair, replacement or Store Credit."
      ),
      clause(
        "Replacement and Store Credit are subject to depreciation under clause 1.11, which applies instead of the deductible."
      ),
      clause(
        "Store Credit is valid for 6 months from the date of issue, usable only on IZI Cart, not transferable and not redeemable for cash."
      ),
      clause(
        "Protega may request submission of the damaged product or parts for inspection."
      ),
      clause(
        "Replaced parts, and any product replaced or settled by Store Credit, become the property of Protega."
      ),
      clause(
        "Where the product is handed over incomplete, the value of missing parts is deducted from the resolution."
      ),
      clause(
        "If inspection finds no fault, or finds that the damage is not covered, the claim is rejected and the Customer bears the inspection and logistics costs before the product is returned."
      ),
      clause(
        "If the Customer does not accept delivery or respond within 7 days of Protega’s written notice after repair or rejection, Protega may charge reasonable storage costs from that date, and any further delivery attempts will be charged to the Customer. Protega will send a final written notice before disposal, and if the product is still not collected within 10 days of the first notice, Protega may dispose of or recycle it."
      ),
      clause(
        "Where Protega schedules a pickup, doorstep repair visit, or delivery of a repaired or replacement product, the Customer must be available at the time discussed with the Customer before the visit is scheduled, or reschedule at least 24 hours in advance. If the Customer is unavailable, refuses to accept, or does not provide access, the cost of each further attempt is borne by the Customer and must be paid before the next attempt is scheduled. After two missed attempts, Protega may close the claim as withdrawn or, where the product has already been repaired or replaced, apply the storage and disposal terms above.",
        "Missed appointments:"
      ),
      clause(
        "Protega may reset the device and update its firmware or app during repair, and is not liable for loss of data, footage or settings."
      ),
      clause(
        "If spare parts are no longer available, Protega may resolve the claim by replacement or Store Credit."
      ),
      clause(
        "Protega will make reasonable efforts to meet the timelines in this Plan, but is not liable for delays caused by unavailability of spare parts, logistics disruptions or events beyond its reasonable control."
      ),
      clause(
        "Protega retains the right to recover costs from responsible third parties."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "cancellation",
    title: "Cancellation",
    blocks: [
      clause(
        "Customers may cancel the Plan within seven (7) days of purchase for a full refund, provided no claim has been raised."
      ),
      clause(
        "If the product is returned or the order is cancelled, the Plan is cancelled automatically and the Plan fee refunded in full, provided no claim has been raised."
      ),
      clause(
        "The Plan becomes non-refundable once a claim has been raised, or after the 7-day period."
      ),
      clause(
        "Protega reserves the right to cancel coverage in cases of fraud, misrepresentation or material non-disclosure. On such cancellation, the Plan fee is forfeited and Protega may recover all costs incurred on any claim."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "conditions",
    title: "Conditions",
    blocks: [
      clause(
        "The Plan is non-transferable and applies only to the original purchaser and the covered product."
      ),
      clause("Renewal of the Plan is not available upon expiry."),
      clause(
        "The Plan is valid only within India. Pickup and on-site service are available only at Serviceable Locations; elsewhere, Protega may arrange courier pickup or ask the Customer to send the product."
      ),
      clause(
        "Protega reserves the right to request additional documentation or inspection where required."
      ),
      clause(
        "Any misuse, suppression of facts or violation of Plan terms may result in denial of service."
      ),
      clause(
        "All notices and communication are sent to the Customer’s registered email address, mobile number or WhatsApp number and are treated as received when sent."
      ),
      clause(
        "Protega may appoint Authorised Service Partners to perform any part of the Plan."
      ),
      clause(
        "Protega is not liable for any failure or delay caused by events beyond its reasonable control, including natural disasters, epidemics, government action, strikes or disruption to logistics."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "legal-disclosures",
    title: "Legal disclosures",
    blocks: [
      clause(
        "This Plan shall be governed by and construed in accordance with the laws of India. Subject to arbitration, courts at Mumbai have exclusive jurisdiction."
      ),
      clause(
        "Any dispute, difference or claim arising out of or relating to this Plan shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996, by a sole arbitrator appointed by mutual agreement or as provided under that Act. The seat and venue of arbitration is Mumbai and the language is English."
      ),
      clause(
        "Nothing in these Terms limits the Customer’s rights or remedies under the Consumer Protection Act, 2019."
      ),
      clause(
        "Protega and IZI Cart shall not be liable for any indirect, incidental, special or consequential losses, including loss of data or loss of use."
      ),
      clause(
        "The Plan and purchase confirmation constitute the entire agreement. If any provision of this Plan is held to be invalid or unenforceable, the remaining provisions shall remain valid and enforceable."
      ),
      clause(
        "Customer data is processed as per the Protega Privacy Policy. Personal data, claim photos and videos are shared with IZI Cart and Authorised Service Partners only to administer the Plan, verify claims and prevent fraud. Calls and WhatsApp conversations may be recorded for quality and verification."
      ),
      clause(
        "Where Protega relies on an exclusion to reject a claim, the Customer must show that the claim is covered."
      ),
      clause(
        "The Customer indemnifies Protega against loss caused by the Customer’s fraud, misrepresentation or breach of these Terms."
      ),
      clause(
        "Protega may amend these Terms as needed, following regulatory requirements. Amendments will not reduce cover on Plans already issued, except where required by law."
      ),
      clause(
        "A failure or delay by Protega to enforce any term is not a waiver of that term."
      ),
    ],
  },
  {
    kind: "faq",
    id: "faq",
    title: "FAQ",
    items: [
      {
        q: "What does the Accidental Damage Protection Plan cover?",
        a: "The Plan covers sudden, unforeseen and unintentional physical damage caused by external incidents such as drops, falls or collisions that result in functional or structural damage to the product, subject to the terms and exclusions of the Plan.",
      },
      {
        q: "When does cover start?",
        a: "Cover starts as soon as you place the order and lasts for 12 or 24 months, depending on the plan selected. It covers only accidental damage that happens after delivery, during normal intended use. Damage in transit is not covered.",
      },
      {
        q: "Does this Plan cover manufacturing defects or performance issues?",
        a: "No. Manufacturing defects or performance-related issues are covered under the manufacturer’s standard warranty and are not included under this Plan.",
      },
      {
        q: "Is accidental damage caused due to misuse or negligence covered?",
        a: "No. Damage arising from gross negligence, reckless handling, misuse, or use of the product outside the manufacturer’s intended purpose, operating instructions or stated limitations is not covered under this Plan.",
      },
      {
        q: "Is water damage covered on a waterproof camera?",
        a: "Only accidental liquid damage from a single incident. Water damage from use beyond the rated depth, without the required housing, or with seals or doors not closed is not covered.",
      },
      {
        q: "Do I need to use the product as per the instruction manual to remain eligible?",
        a: "Yes. Coverage under this Plan is applicable only when the product is used strictly in accordance with the manufacturer’s user manual, safety guidelines, intended purpose and recommended operating conditions.",
      },
      {
        q: "How many accidental damage claims can I make under this Plan?",
        a: "You are eligible for one (1) approved accidental damage claim per product during the Policy Term.",
      },
      {
        q: "How soon should I report an accidental damage incident?",
        a: "All accidental damage incidents must be reported to Protega within seventy-two (72) hours of occurrence, along with supporting documentation.",
      },
      {
        q: "How long does the claim resolution process take?",
        a: "Standard repairs are typically completed within 5–7 working days. Replacements depend on product availability, and complex cases are subject to operational review.",
      },
      {
        q: "What happens if my product cannot be repaired?",
        a: "Protega may, at its discretion, provide a new, refurbished or comparable replacement, or issue store credit up to the Invoice Value of the product.",
      },
      {
        q: "Is depreciation applied on a replacement?",
        a: "Yes. Replacement or store credit is reduced by depreciation based on the product’s age, instead of the deductible: 10% up to 6 months, 20% for 6–12 months, 30% for 12–18 months and 40% for 18–24 months. Repairs are not depreciated.",
      },
      {
        q: "Can I get cash instead of a repair?",
        a: "No. Claims are resolved only by repair, replacement or store credit.",
      },
      {
        q: "Can I cancel my Accidental Damage Protection Plan?",
        a: "Yes. You may cancel the Plan within seven (7) days of purchase provided no claim has been raised. The Plan becomes non-refundable thereafter.",
      },
      {
        q: "What if I return my product?",
        a: "The Plan is cancelled automatically and the Plan fee is refunded, provided no claim has been raised.",
      },
      {
        q: "Is this Plan transferable to another person or product?",
        a: "No. This Plan is non-transferable and applies only to the original purchaser and the covered product.",
      },
      {
        q: "Is there any deductible applicable on claims?",
        a: "Yes. Each approved claim is subject to a deductible equal to ten percent (10%) of the Invoice Value or ₹500, whichever is lower, payable within 7 days of claim approval.",
      },
    ],
  },
  slaSection,
  {
    kind: "text",
    id: "grievance-redressal",
    title: "Grievance redressal",
    text: "If you are not satisfied with a claim decision or with our service, write to support@protega.ai with your Policy ID. Every complaint receives a ticket number, is acknowledged within 48 hours and is resolved within 30 days.",
  },
  contactSection,
];
